import { useState } from 'react';
import {
  BEREICHE,
  BEREICH_NAMEN,
  ROLLEN,
  ROLLEN_NAMEN,
  ROLLEN_STANDARD,
  type Bereich,
  type MeinProjekt,
  type Mitglied,
  type Rolle,
  type Stufe,
} from '@beholder/shared';
import { api } from '../api';
import { useIch } from '../sitzung';
import { Symbol } from '../symbole';
import { Auswahl, Dialog, Feld, Knopf, Laden, Meldung, useAktion, useDaten } from '../ui';

const STUFE_NAMEN: Record<Stufe, string> = { kein: 'Kein Zugriff', lesen: 'Lesen', schreiben: 'Lesen & Schreiben' };

export function Mitglieder({ projekt }: { projekt: MeinProjekt }) {
  const ich = useIch();
  const pfad = `/api/projekte/${projekt.id}/mitglieder`;
  const { daten, setDaten, fehler } = useDaten(() => api.get<Mitglied[]>(pfad), [pfad]);
  const [bearbeiten, setBearbeiten] = useState<Mitglied | 'neu' | null>(null);

  return (
    <div className="seite">
      <header className="seiten-kopf">
        <h2>Mitglieder</h2>
        {projekt.verwalten && (
          <Knopf art="haupt" onClick={() => setBearbeiten('neu')}>
            {Symbol.plus} Mitglied hinzufügen
          </Knopf>
        )}
      </header>
      <Meldung>{fehler}</Meldung>
      {!daten && <Laden />}
      <div className="tabelle-rahmen">
        <table className="tabelle">
          <thead>
            <tr>
              <th>Name</th>
              <th>Rolle</th>
              {projekt.verwalten && BEREICHE.map((b) => <th key={b}>{BEREICH_NAMEN[b]}</th>)}
              {projekt.verwalten && <th>KI darf ändern</th>}
              {projekt.verwalten && <th />}
            </tr>
          </thead>
          <tbody>
            {daten?.map((m) => (
              <tr key={m.benutzerId}>
                <td>
                  <strong>{m.anzeigename}</strong> <span className="leise klein">@{m.benutzername}</span>
                  {m.benutzerId === ich.id && <span className="marke">du</span>}
                </td>
                <td>{ROLLEN_NAMEN[m.rolle]}</td>
                {projekt.verwalten &&
                  BEREICHE.map((b) => (
                    <td key={b} className={m.abweichungen[b] ? 'abweichend' : ''}>
                      {STUFE_NAMEN[m.rechte[b]]}
                    </td>
                  ))}
                {projekt.verwalten && <td>{m.kiDirekt ? 'ja' : 'nur Vorschläge'}</td>}
                {projekt.verwalten && (
                  <td>
                    <Knopf klein onClick={() => setBearbeiten(m)}>
                      Bearbeiten
                    </Knopf>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {projekt.verwalten && (
        <p className="leise klein">
          Rollen geben Standardrechte (Leitung und Mitglied: lesen & schreiben, Gast: lesen). Abweichende Einzelrechte sind markiert.
          Die KI-Hilfe arbeitet immer nur mit den Rechten des fragenden Mitglieds.
        </p>
      )}
      {bearbeiten && (
        <MitgliedDialog
          projekt={projekt}
          mitglied={bearbeiten === 'neu' ? null : bearbeiten}
          vorhandene={daten ?? []}
          schliessen={() => setBearbeiten(null)}
          gespeichert={(liste) => {
            setDaten(liste);
            setBearbeiten(null);
          }}
        />
      )}
    </div>
  );
}

function MitgliedDialog({
  projekt,
  mitglied,
  vorhandene,
  schliessen,
  gespeichert,
}: {
  projekt: MeinProjekt;
  mitglied: Mitglied | null;
  vorhandene: Mitglied[];
  schliessen(): void;
  gespeichert(liste: Mitglied[]): void;
}) {
  const auswahl = useDaten(
    () => (mitglied ? Promise.resolve([]) : api.get<{ id: string; benutzername: string; anzeigename: string }[]>('/api/benutzer/auswahl')),
    [],
  );
  const [benutzerId, setBenutzerId] = useState(mitglied?.benutzerId ?? '');
  const [rolle, setRolle] = useState<Rolle>(mitglied?.rolle ?? 'mitglied');
  const [abw, setAbw] = useState<Partial<Record<Bereich, Stufe>>>(mitglied?.abweichungen ?? {});
  const [kiDirekt, setKiDirekt] = useState(mitglied?.kiDirekt ?? false);
  const a = useAktion();

  const kandidaten = (auswahl.daten ?? []).filter((b) => !vorhandene.some((m) => m.benutzerId === b.id));
  const stufe = (b: Bereich): Stufe => abw[b] ?? ROLLEN_STANDARD[rolle];

  async function speichern() {
    const liste = await a.ausfuehren(() =>
      api.put<Mitglied[]>(`/api/projekte/${projekt.id}/mitglieder/${benutzerId}`, { rolle, abweichungen: abw, kiDirekt }),
    );
    if (liste) gespeichert(liste);
  }
  async function entfernen() {
    if (!mitglied || !confirm(`${mitglied.anzeigename} aus dem Projekt entfernen?`)) return;
    const liste = await a.ausfuehren(() => api.delete<Mitglied[]>(`/api/projekte/${projekt.id}/mitglieder/${mitglied.benutzerId}`));
    if (liste) gespeichert(liste);
  }

  return (
    <Dialog
      titel={mitglied ? `${mitglied.anzeigename} bearbeiten` : 'Mitglied hinzufügen'}
      offen
      schliessen={schliessen}
      breit
      fuss={
        <>
          {mitglied && (
            <Knopf art="gefahr" onClick={entfernen} disabled={a.laeuft}>
              Entfernen
            </Knopf>
          )}
          <span className="abstand" />
          <Knopf onClick={schliessen}>Abbrechen</Knopf>
          <Knopf art="haupt" onClick={speichern} disabled={!benutzerId || a.laeuft}>
            Speichern
          </Knopf>
        </>
      }
    >
      <div className="stapel">
        {!mitglied && (
          <Feld titel="Benutzer" hinweis={kandidaten.length === 0 && auswahl.daten ? 'Alle Benutzer sind schon Mitglied. Neue Konten legt ein Admin an.' : undefined}>
            <Auswahl value={benutzerId} onChange={(e) => setBenutzerId(e.target.value)}>
              <option value="">– auswählen –</option>
              {kandidaten.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.anzeigename} (@{b.benutzername})
                </option>
              ))}
            </Auswahl>
          </Feld>
        )}
        <Feld titel="Rolle">
          <Auswahl value={rolle} onChange={(e) => setRolle(e.target.value as Rolle)}>
            {ROLLEN.map((r) => (
              <option key={r} value={r}>
                {ROLLEN_NAMEN[r]}
              </option>
            ))}
          </Auswahl>
        </Feld>
        <fieldset className="rechte-raster">
          <legend>Rechte je Bereich</legend>
          {BEREICHE.map((b) => (
            <label key={b} className="rechte-zeile">
              <span>{BEREICH_NAMEN[b]}</span>
              <Auswahl
                value={stufe(b)}
                onChange={(e) => {
                  const s = e.target.value as Stufe;
                  const neu = { ...abw };
                  if (s === ROLLEN_STANDARD[rolle]) delete neu[b];
                  else neu[b] = s;
                  setAbw(neu);
                }}
              >
                {(['kein', 'lesen', 'schreiben'] as const).map((s) => (
                  <option key={s} value={s}>
                    {STUFE_NAMEN[s]}
                    {s === ROLLEN_STANDARD[rolle] ? ' (Standard)' : ''}
                  </option>
                ))}
              </Auswahl>
            </label>
          ))}
        </fieldset>
        <label className="schalter">
          <input type="checkbox" checked={rolle === 'leitung' || kiDirekt} disabled={rolle === 'leitung'} onChange={(e) => setKiDirekt(e.target.checked)} />
          <span>KI darf für dieses Mitglied direkt ändern (sonst nur Vorschläge zum Bestätigen){rolle === 'leitung' ? ' – bei Leitung immer an' : ''}</span>
        </label>
        <Meldung>{a.fehler}</Meldung>
      </div>
    </Dialog>
  );
}
